import type { FC, ReactNode } from 'react'

import type { WhiteLabel } from '@skytech/types'
import { twMerge } from 'tailwind-merge'

export type BadgeType = 'neutral' | 'positive' | 'negative' | 'info' | 'critical' | 'outline'
export type BadgeSize = 'small' | 'regular'

export const Badge: FC<{
  type: BadgeType
  children?: ReactNode
  className?: string
  whiteLabel?: WhiteLabel
  size?: BadgeSize
  textWrapperClassName?: string
  dot?: boolean
}> = ({
  whiteLabel,
  className,
  children,
  type = 'neutral',
  size = 'regular',
  dot,
  textWrapperClassName,
}) => {
  // todo: replace with the org from url when ready
  const storedWhiteLabel = localStorage.getItem('org') as WhiteLabel
  const wl = whiteLabel || storedWhiteLabel || 'control'

  return (
    <div
      className={twMerge(
        'w-fit rounded-2xl text-base',
        statusColorClasses[type],
        sizeClasses[size],
        (wl === 'threeStepIT' || wl === 'telia') && statusColorClassesWl[wl]?.[type],
        className
      )}
    >
      <div
        className={twMerge(
          'flex items-center justify-start gap-1 px-2 text-center',
          textWrapperClassName
        )}
      >
        {dot && (
          <span
            className={twMerge(
              'h-1.5 w-1.5 shrink-0 rounded-full',
              dotColorClasses[type],
              (wl === 'threeStepIT' || wl === 'telia') && dotColorClassesWl[wl]?.[type]
            )}
          />
        )}
        {children}
      </div>
    </div>
  )
}

const statusColorClasses: Record<BadgeType, string> = {
  negative: 'bg-red-100 text-red-600',
  positive: 'bg-teal-100 text-green-800',
  neutral: 'bg-amber-100 text-yellow-600',
  info: 'bg-gray-200 text-gray-600',
  outline: 'border border-solid border-gray-400 text-gray-600',
  critical: 'bg-red-200 text-red-900',
}

const dotColorClasses: Record<BadgeType, string> = {
  negative: 'bg-red-600',
  positive: 'bg-green-800',
  neutral: 'bg-yellow-600',
  info: 'bg-gray-600',
  outline: 'bg-gray-600',
  critical: 'bg-red-900',
}

const sizeClasses: Partial<Record<BadgeSize, string>> = {
  small: 'text-sm',
}

const statusColorClassesWl: Partial<Record<WhiteLabel, Record<BadgeType, string>>> = {
  threeStepIT: {
    negative: 'bg-[#eecbcb] text-threeStepIT-failure',
    positive: 'bg-[#d3f3eb] text-threeStepIT-success',
    neutral: 'bg-threeStepIT-warning-light text-threeStepIT-warning',
    info: 'bg-[#dce9f5] text-threeStepIT-neutral',
    critical: 'bg-[#dba0a0] text-[#4a1414]',
    outline: 'border border-threeStepIT-neutral text-threeStepIT-neutral',
  },
  telia: {
    negative: 'font-telia',
    positive: 'font-telia bg-telia-success text-white',
    neutral: 'font-telia',
    info: 'font-telia',
    critical: 'font-telia',
    outline: 'font-telia',
  },
}

const dotColorClassesWl: Partial<Record<WhiteLabel, Record<BadgeType, string>>> = {
  threeStepIT: {
    negative: 'bg-threeStepIT-failure',
    positive: 'bg-threeStepIT-success',
    neutral: 'bg-threeStepIT-warning',
    info: 'bg-threeStepIT-neutral',
    critical: 'bg-[#4a1414]',
    outline: 'bg-threeStepIT-neutral',
  },
  telia: {
    negative: 'font-telia',
    positive: 'font-telia bg-white',
    neutral: 'font-telia',
    info: 'font-telia',
    critical: 'font-telia',
    outline: 'font-telia',
  },
}
